import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { ragGenerateThreadId, ragPostQuery, ragUploadPdfs } from "./action";
import jsCookie from "js-cookie";
import remarkGfm from "remark-gfm";
import Markdown from "react-markdown";
import rehypeExternalLinks from "rehype-external-links";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import ArrowLeftIcon from "src/components/icons/common/arrow-left";
import AgentHead from "../ai-agent/AgentHead";
import PN from "../../../assets/images/pn.svg";
import UserIcon from "src/components/reusable/userIcon";
import DotLoader from "src/components/reusable/dot-loader";
import classNames from "classnames";
import SentIcon from "src/components/icons/common/send";
import FileUploadSection from "./UploadFile";
import CustomMarkdown from "./CustomMarkdown";
import ragIcon from "../ai-agent/landing/_assets/AgentLanding-Icons/Customer360.svg";

type Message = {
    sender: "user" | "system";
    text: string;
    repStatement?: string;
    isInfo?: boolean
    sources?: {
        title: string;
        url: string;
        confidence?: number;
        snippet?: string;
    }[];
};

const RAGChat: React.FC = () => {
    const dispatch = useAppDispatch();
    const userDetail = useAppSelector((state) => state.auth.user);

    // Agent configuration
    const agentName = "Rag Chatbot";
    const agentIcons: Record<string, string> = {
        "Rag Chatbot": ragIcon,
    };
    const iconSrc = agentIcons[agentName] || ragIcon;

    const userId = jsCookie.get("user_id");
    const [fileUploaded, setFileUploaded] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [threadId, setThreadId] = useState<string | null>(null);
    const [sources, setSources] = useState<any[]>([

    ]);

    useEffect(() => {
        const initThread = async () => {
            try {
                const res = await dispatch(
                    ragGenerateThreadId({ userId: userId || "" })
                ).unwrap();

                if (res.data?.thread_id) {
                    setThreadId(res.data.thread_id);
                } else {
                    throw new Error("Failed to get thread ID");
                }
            } catch (err) {
                console.error("Thread init error:", err);
                alert("Failed to initialize chat session.");
            }
        };

        initThread();
    }, []);


    const handleSend = async () => {


        if (!query.trim() || !threadId) return;

        const userMsg: Message = { sender: "user", text: query };
        setMessages((prev) => [...prev, userMsg]);
        setQuery("");
        setLoading(true);

        try {
            const res = await dispatch(
                ragPostQuery({
                    question: query,
                    userId: userId || "",
                    threadId,
                })
            ).unwrap();

            const data = res.data || {};

            const sources = data.sources?.sort((a: any, b: any) => a.page - b.page) || [];
            const scores: any = data.scores || {};
            const summary = data.summary || "";

            setSources(sources);



            const formatLabel = (key: string) =>
                key
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word

            // Format scores if present
            // const hasScores = Object.keys(scores).length > 0;
            //
            // const scoreMarkdown = hasScores
            //     ? "\n\n" +
            //     Object.entries(scores)
            //         .map(
            //             ([key, value]) =>
            //                 `**${formatLabel(key)}**: ${typeof value === "number" ? value.toFixed(2) : value}`
            //         )
            //         .join("\n")
            //     : "";


            setMessages((prev) => [
                ...prev,
                {
                    sender: "system",
                    text: summary,
                    repStatement: typeof data.most_representative_statement === "string" ? data.most_representative_statement : "",
                },
            ]);
        } catch (err) {
            setMessages((prev) => [
                ...prev,
                { sender: "system", text: "Error getting response from server." },
            ]);
        } finally {
            setLoading(false);
        }
    };


    const [sourcesOpen, setSourcesOpen] = useState(false);

    const chatRef = useRef<HTMLInputElement>(null);
    const scrollToBottom = () => {
        if (chatRef.current) {
            chatRef.current.scrollTo({
                top: chatRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    };

    const textareaRef = useRef<any>(null);
    const fileInputRef = useRef<any>(null);


    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const [expandedPages, setExpandedPages] = useState<Record<string, boolean>>({});

    const togglePage = (file: string, page: number) => {
        const key = `${file}-${page}`;
        setExpandedPages((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

   const groupedSources = sources?.reduce<Record<string, any[]>>((acc, curr) => {
    if (!acc[curr.filename]) acc[curr.filename] = [];

    acc[curr.filename].push(curr);

    // sort each group right after inserting
    acc[curr.filename].sort((a, b) => {
        if (a._final_rank_score == null && b._final_rank_score == null) return 0;
        if (a._final_rank_score == null) return 1; // push a to bottom
        if (b._final_rank_score == null) return -1; // push b to bottom
        return b._final_rank_score - a._final_rank_score; // higher first
    });

    return acc;
}, {});

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" && !event.shiftKey) {
            handleSend()
            event.preventDefault();
        }
    };

    return (
        <>
            <div className="w-full">
                <div className="flex justify-between items-start">
                    <div className="inline-flex justify-start items-center mt-2 mb-2 flex-0">
                        <Link to="/">
                            <p className="mr-4 text-secondary-800 flex items-center min-h-4">
                                <ArrowLeftIcon className="mr-1" />
                                Back
                            </p>
                        </Link>
                    </div>


                    <AgentHead
                        agentName="Rag Chatbot"
                        className="flex-1"
                    />
                </div>
                <div className={`px-0 md:px-3 mx-auto h-full flex flex-col lg:flex-row gap-4 ${!sources?.length ? "lg:w-[70%] md:w-full" : "w-full"}`}>
                    <div className={classNames(
                        "flex-auto",
                        sources?.length > 0 ? "lg:w-[50%]" : "lg:w-[100%]"
                    )}>
                        <div className="">


                            <div className="flex flex-col gap-y-3 lg:flex-row lg:gap-y-0 gap-x-5 lg:gap-x-2">
                                <div className="w-full mx-auto flex-auto relative flex flex-col gap-2 min-h-[100vh]">
                                    <div className="flex flex-row flex-auto justify-center">
                                        <div
                                            ref={chatRef}
                                            className="bg-white rounded-lg p-3 pt-0 w-full max-h-[calc(100vh-244px)] overflow-y-auto pn_scroller shadow-[0_4px_4px_0] shadow-[#000]/[0.06]"
                                        >
                                            <FileUploadSection threadId={threadId} userId={userId} setFileUploaded={(value: boolean) => {
                                                setFileUploaded(value);
                                                setMessages([
                                                    {
                                                        sender: "system",
                                                        text: "Files uploaded successfully. You can now start query.",
                                                        isInfo: true
                                                    }
                                                ])

                                            }} fileuploaded={fileUploaded} />
                                            <div className="mt-3">

                                                {messages.map((msg, index) => (
                                                    <div key={index} className={`space-y-2`}>
                                                        {msg.sender === "system" ?
                                                            <>
                                                                <div className="flex items-center justify-start gap-2 ">
                                                                    <div className="pt-3 shrink-0 ">
                                                                        <img className="h-3 w-3 " src={PN} alt={"Pn"} />
                                                                    </div>

                                                                    <div
                                                                        className={`shadow-md border border-gray-200 bg-white  ${index === 0 ? "" : "mt-2"
                                                                            } rounded-2xl rounded-bl-none flex items-center justify-center px-4 py-2 gap-2 relative  `}
                                                                    >
                                                                        <div className=" w-full">
                                                                            {msg.repStatement && <> <h3 className="font-semibold text-[1.125em] my-[0.5em]">Most Representative Statement:</h3><p className="mb-[0.6rem] text-[#5f6465]"><CustomMarkdown text={`${msg.repStatement}`} /></p></>}

                                                                            {msg.isInfo ? <p className="mb-[0.6rem] text-[#5f6465]"><CustomMarkdown text={`${msg.text}`} /></p> :

                                                                                <>
                                                                                    {sources?.length > 0 ? <h3 className="font-semibold text-[1.125em] my-[0.5em]">Summary:</h3> : null}
                                                                                    <p className="mb-[0.6rem] text-[#5f6465]"><CustomMarkdown text={`${msg.text}`} /></p>
                                                                                </>
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </> : <>
                                                                <div className={`flex flex-wrap items-center justify-end gap-2 ${index === 0 ? "" : "mt-2"}`}>
                                                                    <div
                                                                        className={`rounded-2xl rounded-br-none whitespace-pre-line flex items-center justify-center px-4 py-2 gap-2 relative select-text bg-foundationOrange-100`}
                                                                    >
                                                                        {msg.text}
                                                                    </div>
                                                                    <div className="pt-3 shrink-0">
                                                                        <UserIcon
                                                                            first_name={userDetail?.first_name || ""}
                                                                            last_name={userDetail?.last_name || ""}
                                                                            profile_photo={userDetail?.profile_photo}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </>}


                                                    </div>
                                                ))}

                                                {loading && (
                                                    <div className="flex items-center justify-center p-5 h-full">
                                                        <DotLoader />
                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-center mt-auto">

                                        <div className="flex flex-col w-full mb-[20px]">

                                            <div
                                                className="w-full flex flex-col rounded-lg border border-gray-200 shadow-inputBox overflow-hidden bg-white relative mb-[0px]"
                                            >
                                                <div className="flex items-center p-1">
                                                    <textarea
                                                        ref={textareaRef}
                                                        onKeyDown={handleKeyDown}
                                                        onChange={(e) => setQuery(e.target.value)}
                                                        disabled={!fileUploaded || loading}
                                                        value={query}
                                                        className={classNames(
                                                            "appearance-none leading-tight w-full h-full p-2 border-none rounded-md bg-white placeholder:text-appGray-600 focus:border-none focus-visible:border-none focus:outline-none focus:ring-0",
                                                            (!fileUploaded || loading) ? "cursor-not-allowed" : "",
                                                        )}
                                                        placeholder="Type here"
                                                    />
                                                </div>

                                                <div className="bg-appGray-100 rounded-b-lg p-2 flex items-center flex-wrap gap-2 py-3">



                                                    <div className="absolute right-2 py-2">
                                                        <button
                                                            className={`bg-primary-900 text-white rounded-full h-4 w-4 flex items-center justify-center cursor-pointer`}
                                                            type="button"
                                                            onClick={handleSend}
                                                        >
                                                            <SentIcon className="h-2 w-2" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>

                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                    {
                        sources?.length > 0 && (
                            <div className="lg:w-[40%] w-full lg:mt-0 overflow-y-auto pn_scroller h-[90%] lg:max-h-[70vh] lg:h-auto">
                                <div className="mt-2 border border-[#D7D7D7] rounded-md shadow-sm">
                                    <button
                                        onClick={() => setSourcesOpen((prev) => !prev)}
                                        className="w-full flex justify-between items-center px-2 py-2 bg-white  rounded-t-md"
                                    >
                                        <span className="font-medium text-lg text-[#120824]">Sources</span>
                                        {sourcesOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>

                                    {sourcesOpen && (
                                        <div className="bg-white px-2 py-1 space-y-4 rounded-b-md text-sm text-gray-700 border-t border-t-[#D7D7D7]">
                                            {Object.entries(groupedSources).map(([filename, entries]) => (
                                                <div key={filename} className="border border-[#D7D7D7] rounded-md p-1 space-y-1 mb-2">
                                                    <div className="bg-gray-50 pt-1 text-base font-medium text-[#120824]">{filename}</div>

                                                    {entries.map(({ page, text, _final_rank_score }, index: number) => {
                                                        const key = `${filename}-${index}`;
                                                        const isExpanded = expandedPages[key];

                                                        return (
                                                            <div key={key} className="border border-[#DBDEEB] rounded">
                                                                <button
                                                                    onClick={() => togglePage(filename, index)}
                                                                    className="flex items-center justify-between w-full p-1 hover:bg-gray-100"
                                                                >
                                                                    <span className="text-sm font-semibold text-[#442873]">Page No. {page}</span>
                                                                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                                </button>

                                                                {isExpanded && (
                                                                    <div className="border-t border-t-[#DBDEEB] p-1 whitespace-pre-wrap text-gray-800 text-sm">
                                                                        <CustomMarkdown text={text || "No content found in this snippet."} />
                                                                        <div className="flex flex-wrap items-center gap-1">
    <p><strong>Relevant Score:</strong>{_final_rank_score || "-"}</p>
 </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                </div>
            </div>
        </>
    );
};

export default RAGChat;
